import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { APP_ORIGIN, type SiteLink } from '@bookkeeper/shared/site-chrome';
import { useLandingNavigationPaths } from '@/hooks/useLandingNavigationPaths';
import { useTranslation, type TranslationKey } from '@/i18n/i18n';
import { isProxyOwnedLegalPath } from '@/i18n/paths';
import { wwwUrl } from '@/lib/landing-urls';

/**
 * One header or footer item from the shared www chrome (`@bookkeeper/shared/site-chrome`),
 * rendered for the landing SPA: landing routes through the router, everything another app
 * serves (blog, hub, calculators) as a plain document link.
 */
export function SiteChromeLink({
  link,
  className,
  onClick,
  children,
  testId,
}: {
  link: SiteLink;
  className?: string;
  onClick?: () => void;
  /** Defaults to the link's translated label. */
  children?: ReactNode;
  testId?: string;
}) {
  const { t, locale } = useTranslation();
  const { localizedPath, withSearch, homeHash } = useLandingNavigationPaths();
  const label = children ?? t(link.labelKey as TranslationKey);
  const common = { className, onClick, 'data-testid': testId, 'data-site-link': link.id };
  const { target } = link;

  switch (target.kind) {
    case 'page':
      return (
        <Link to={withSearch(localizedPath(target.path))} {...common}>
          {label}
        </Link>
      );
    case 'legal': {
      const to = localizedPath(target.path);
      return (
        <Link to={to} reloadDocument={isProxyOwnedLegalPath(to)} {...common}>
          {label}
        </Link>
      );
    }
    case 'section':
      return (
        <a href={homeHash(target.hash)} {...common}>
          {label}
        </a>
      );
    case 'www':
      return (
        <a href={wwwUrl(target.path, target.localized ? locale : null)} {...common}>
          {label}
        </a>
      );
    case 'app':
      return (
        <a
          href={`${import.meta.env.VITE_APP_URL || APP_ORIGIN}${localizedPath(target.path)}`}
          {...common}
        >
          {label}
        </a>
      );
    case 'external':
      return (
        <a href={target.href} rel={target.rel} {...common}>
          {label}
        </a>
      );
  }
}
